import {
  Action,
  combineReducers,
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {selectCurrentCatalogId} from '../../common/catalog/catalog.selectors';
import * as cartSelector from '../../common/shopping-cart/cart.selectors';
import {selectCartItems} from '../../common/shopping-cart/cart.selectors';
import * as errorSelector from '../../common/error-handling/error-handling.selectors';
import {
  CartItem,
  Product,
  ProductComponentModel,
  ProductType,
  ServiceOffer,
  serviceOfferComponentKey,
  Variant,
} from '../../data/model';
import * as fromAddOns from './add-on.reducer';
import * as fromLoading from './loading.reducer';

export const addOnFeatureKey = 'add-on-feature';

export interface CommerceState {
  [fromAddOns.addOnKey]: fromAddOns.AddOnState;
  [fromLoading.loadingKey]: fromLoading.LoadingState;
}

export function reducers(
  state: CommerceState | undefined,
  action: Action,
): CommerceState {
  return combineReducers({
    [fromAddOns.addOnKey]: fromAddOns.reducer,
    [fromLoading.loadingKey]: fromLoading.reducer,
  })(state, action);
}

export const selectCommerceState =
  createFeatureSelector<CommerceState>(addOnFeatureKey);

//region - loading state -
export const selectLoadingState = createSelector(
  selectCommerceState,
  state => state[fromLoading.loadingKey],
);

export const selectApplicationLoading = createSelector(
  selectLoadingState,
  fromLoading.getIsLoading,
);
//endregion - loading state -

//region - add-on state -
export const selectAddOnState = createSelector(
  selectCommerceState,
  state => state[fromAddOns.addOnKey],
);

export const selectAddOns = createSelector(
  selectAddOnState,
  fromAddOns.getAddOns,
);

export const selectAddOnCount = createSelector(selectAddOns, addOns =>
  !addOns ? 0 : addOns.length,
);

export const selectCurrentIndex = createSelector(
  selectAddOnState,
  fromAddOns.getCurrentIndex,
);

export const selectAddonLoadingComplete = createSelector(
  selectAddOnState,
  fromAddOns.getAddonLoadingComplete,
);

export const selectCurrentPage = createSelector(
  selectCurrentIndex,
  (index: number) => index + 1,
);

export const selectProductComponent = createSelector(
  selectAddOns,
  selectCurrentIndex,
  (addOns: ProductComponentModel<ProductType>[] | undefined, index: number) =>
    !addOns || addOns.length === 0 ? undefined : addOns[index],
);

export const showNextButton = createSelector(
  selectCurrentPage,
  selectAddOnCount,
  (currentPage: number, count: number) => {
    return currentPage < count;
  },
);

export const showPrevButton = createSelector(
  selectCurrentIndex,
  (currentIndex: number) => currentIndex > 0,
);

export const selectServiceOffers = createSelector(
  selectAddOns,
  (addons: ProductComponentModel<ProductType>[]) => {
    return addons
      .filter(
        addon =>
          addon.componentKey === serviceOfferComponentKey &&
          (<ServiceOffer>addon.data).quoteRequired(),
      )
      .map(model => model.data as ServiceOffer);
  },
);

export const selectAddonByCatalogId = createSelector(
  selectAddOns,
  selectCurrentCatalogId,
  (
    addons: ProductComponentModel<ProductType>[] | undefined,
    catalogId: string | undefined,
  ) => {
    return addons?.find(addon => addon.catalogId === catalogId);
  },
);
//endregion - add-on state -

//region - compound selectors -
export const serviceOfferExistsInCart = createSelector(
  selectProductComponent,
  cartSelector.selectCartItems,
  (
    currentAddOn: ProductComponentModel<ProductType> | undefined,
    cartItems: CartItem[] = [],
  ) => {
    if (!currentAddOn?.addToCartOnNext) {
      return false;
    }
    const addonId = currentAddOn?.data
      ? (currentAddOn.data as Product).id
      : undefined;
    const registrationId = currentAddOn?.data
      ? (currentAddOn.data as Product).references?.registration?.id
      : undefined;
    const index = cartItems.findIndex(
      i =>
        i.productId === addonId &&
        i.references?.registration?.id === registrationId,
    );
    return index > -1;
  },
);

export const selectCurrentAddOnFromCart: MemoizedSelector<
  object,
  CartItem | undefined
> = createSelector(
  selectAddonByCatalogId,
  selectCartItems,
  (
    addon: ProductComponentModel<ProductType> | undefined,
    cartItems: CartItem[] = [],
  ) => {
    if (!addon?.addToCartOnNext) {
      return undefined;
    }
    const product = addon.data as Product;
    return cartItems.find(
      (item: CartItem) =>
        item.productId === product.id &&
        item.references?.registration?.id ===
          product.references?.registration?.id,
    );
  },
);

/**
 * Returns the cart item with the current product's id.
 */
export const selectCartItemFromCurrentProduct = createSelector(
  cartSelector.selectCartItems,
  selectProductComponent,
  (
    cartItems: CartItem[],
    productComponent: ProductComponentModel<ProductType> | undefined,
  ) => {
    if (cartItems.length === 0 || !productComponent) {
      return null;
    }
    return cartItems.find(
      item => item.productId === (<Product>productComponent.data).id,
    );
  },
);

// export const addonsFinishedLoading = createSelector()

export const showNoAddonsAvailable = createSelector(
  errorSelector.selectUncaughtError,
  selectAddOnCount,
  selectAddonLoadingComplete,
  (error: unknown, addonCount: number, loadingComplete: boolean) =>
    !!error || (addonCount === 0 && loadingComplete),
);
//endregion - compound selectors -

//region - specs selectors -
export const selectSpecs = createSelector(
  selectAddOnState,
  fromAddOns.getSpecs,
);

export const selectVariants: MemoizedSelector<
  object,
  | {
      [productId: string]: Variant[];
    }
  | undefined
> = createSelector(selectAddOnState, fromAddOns.getVariants);

//endregion - specs state -
